import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  DEMO_RANGE,
  DEMO_STATUS_COLORS,
  DEMO_TASKS,
} from "../data/managementDemoData";
import "./styling/ManagementDemo.css";

/**
 * ManagementDemo — homepage marketing preview of the Management page's Gantt
 * timeline. Renders illustrative tasks (not real customer data) so the shape of
 * the real timeline is visible without an account or a selected project.
 *
 * Deliberately a reduced version of the real chart: it keeps the parts that
 * communicate the feature — dependency arrows, milestone diamonds, the critical
 * path, the today marker, status colours — and drops the editing affordances
 * (drag-to-reschedule, inline edit, zoom levels) that need a real backend.
 */

const ROW_H = 34;
const HEADER_H = 28;
/* Wide enough for the longest demo task name plus its assignee without
   ellipsing — truncated names made the tree look broken rather than dense. */
const NAME_W = 268;

const STEP_ICON = { sequential: "→", concurrent: "⇉" };

const MS_PER_DAY = 86400000;

/** Start of today, so day offsets land on stable date boundaries. */
const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const ManagementDemo = () => {
  const [width, setWidth] = useState(0);
  const [showCritical, setShowCritical] = useState(false);
  const [activeStatus, setActiveStatus] = useState(null);
  const trackRef = useRef(null);

  // Bars are positioned in pixels off a measured width rather than percentages,
  // so the dependency elbows keep square corners and un-skewed arrowheads.
  useLayoutEffect(() => {
    const el = trackRef.current;
    if (!el) return undefined;
    const measure = () => setWidth(el.clientWidth);
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const totalDays = DEMO_RANGE.before + DEMO_RANGE.after;
  const dayToX = useCallback(
    (offset) => ((offset + DEMO_RANGE.before) / totalDays) * width,
    [width, totalDays]
  );

  const today = useMemo(startOfToday, []);

  // Month gridlines/labels across the visible window.
  const months = useMemo(() => {
    if (!width) return [];
    const out = [];
    const first = new Date(today.getTime() - DEMO_RANGE.before * MS_PER_DAY);
    const cursor = new Date(first.getFullYear(), first.getMonth(), 1);
    for (let i = 0; i < 8; i += 1) {
      const offset = Math.round((cursor.getTime() - today.getTime()) / MS_PER_DAY);
      if (offset > DEMO_RANGE.after) break;
      out.push({
        key: `${cursor.getFullYear()}-${cursor.getMonth()}`,
        label: MONTH_NAMES[cursor.getMonth()],
        x: dayToX(offset),
        offset,
      });
      cursor.setMonth(cursor.getMonth() + 1);
    }
    return out.filter((m) => m.offset >= -DEMO_RANGE.before);
  }, [width, today, dayToX]);

  const statusCounts = useMemo(() => {
    const counts = new Map();
    DEMO_TASKS.forEach((t) => counts.set(t.status, (counts.get(t.status) || 0) + 1));
    return [...counts.entries()];
  }, []);

  const rowIndex = useMemo(() => {
    const map = new Map();
    DEMO_TASKS.forEach((t, i) => map.set(t.id, i));
    return map;
  }, []);

  const isDimmed = useCallback(
    (task) => {
      if (activeStatus && task.status !== activeStatus) return true;
      if (showCritical && !task.critical) return true;
      return false;
    },
    [activeStatus, showCritical]
  );

  // Elbow connectors: out of the predecessor's right edge, down/up to the
  // successor's row, then into its left edge.
  const arrows = useMemo(() => {
    if (!width) return [];
    return DEMO_TASKS.filter((t) => t.after && rowIndex.has(t.after)).map((t) => {
      const from = DEMO_TASKS[rowIndex.get(t.after)];
      const fromEnd = from.milestone ? from.at : from.to;
      const toStart = t.milestone ? t.at : t.from;
      const x1 = dayToX(fromEnd);
      const y1 = rowIndex.get(from.id) * ROW_H + ROW_H / 2;
      const x2 = dayToX(toStart);
      const y2 = rowIndex.get(t.id) * ROW_H + ROW_H / 2;
      const midX = Math.max(x1 + 9, x2 - 11);
      return {
        key: `${from.id}-${t.id}`,
        d: `M ${x1} ${y1} H ${midX} V ${y2} H ${x2 - 5}`,
        head: `M ${x2 - 5} ${y2 - 3.5} L ${x2} ${y2} L ${x2 - 5} ${y2 + 3.5} Z`,
        critical: !!(from.critical && t.critical),
        dimmed: isDimmed(from) || isDimmed(t),
      };
    });
  }, [width, dayToX, rowIndex, isDimmed]);

  const chartH = DEMO_TASKS.length * ROW_H;
  const todayX = dayToX(0);

  return (
    <div className="mgmt-demo">
      <div className="mgmt-demo__toolbar">
        <div className="mgmt-demo__chips" role="group" aria-label="Filter by status">
          {statusCounts.map(([status, count]) => (
            <button
              key={status}
              type="button"
              className={`mgmt-demo__chip${activeStatus === status ? " is-active" : ""}`}
              style={{ "--chip-color": DEMO_STATUS_COLORS[status] }}
              onClick={() => setActiveStatus((s) => (s === status ? null : status))}
              aria-pressed={activeStatus === status}
            >
              <span className="mgmt-demo__chip-dot" />
              {status}
              <span className="mgmt-demo__chip-count">{count}</span>
            </button>
          ))}
        </div>
        <button
          type="button"
          className={`mgmt-demo__toggle${showCritical ? " is-active" : ""}`}
          onClick={() => setShowCritical((v) => !v)}
          aria-pressed={showCritical}
        >
          ◆ Critical path
        </button>
      </div>

      <div className="mgmt-demo__chart" style={{ "--name-w": `${NAME_W}px`, "--row-h": `${ROW_H}px` }}>
        <div className="mgmt-demo__names">
          <div className="mgmt-demo__names-head" style={{ height: HEADER_H }}>
            Task
          </div>
          {DEMO_TASKS.map((t) => (
            <div
              key={t.id}
              className={`mgmt-demo__name${isDimmed(t) ? " is-dimmed" : ""}`}
              data-step={t.step || "sequential"}
              style={{ paddingLeft: `${0.6 + t.depth * 0.85}rem` }}
              title={t.assignee ? `Assigned to ${t.assignee}` : undefined}
            >
              <span className="mgmt-demo__step" aria-hidden="true">
                {t.milestone ? "◆" : STEP_ICON[t.step] || "→"}
              </span>
              <span className="mgmt-demo__name-text">{t.name}</span>
              {t.assignee && <span className="mgmt-demo__assignee">{t.assignee}</span>}
            </div>
          ))}
        </div>

        <div className="mgmt-demo__track" ref={trackRef}>
          <div className="mgmt-demo__track-head" style={{ height: HEADER_H }}>
            {months.map((m) => (
              <span key={m.key} className="mgmt-demo__month" style={{ left: m.x }}>
                {m.label}
              </span>
            ))}
          </div>

          <div className="mgmt-demo__rows" style={{ height: chartH }}>
            {months.map((m) => (
              <span key={m.key} className="mgmt-demo__gridline" style={{ left: m.x }} />
            ))}

            {DEMO_TASKS.map((t, i) => (
              <span
                key={`band-${t.id}`}
                className={`mgmt-demo__band${i % 2 ? " is-alt" : ""}`}
                style={{ top: i * ROW_H, height: ROW_H }}
              />
            ))}

            {width > 0 && (
              <svg className="mgmt-demo__arrows" width={width} height={chartH} aria-hidden="true">
                {arrows.map((a) => (
                  <g
                    key={a.key}
                    className={`mgmt-demo__arrow${a.critical ? " is-critical" : ""}${
                      a.dimmed ? " is-dimmed" : ""
                    }`}
                  >
                    <path d={a.d} fill="none" />
                    <path d={a.head} className="mgmt-demo__arrow-head" />
                  </g>
                ))}
              </svg>
            )}

            {width > 0 &&
              DEMO_TASKS.map((t, i) => {
                const color = DEMO_STATUS_COLORS[t.status];
                const dimmed = isDimmed(t);
                const critical = showCritical && t.critical;
                const top = i * ROW_H;

                if (t.milestone) {
                  return (
                    <span
                      key={t.id}
                      className={`mgmt-demo__milestone${dimmed ? " is-dimmed" : ""}${
                        critical ? " is-critical" : ""
                      }`}
                      style={{ left: dayToX(t.at), top: top + ROW_H / 2, background: color }}
                      title={`${t.name} — ${t.status}`}
                    />
                  );
                }

                const x = dayToX(t.from);
                const w = Math.max(6, dayToX(t.to) - x);
                return (
                  <span
                    key={t.id}
                    className={`mgmt-demo__bar${dimmed ? " is-dimmed" : ""}${
                      critical ? " is-critical" : ""
                    }`}
                    style={{ left: x, width: w, top: top + (ROW_H - 20) / 2, background: color }}
                    title={`${t.name} — ${t.status}`}
                  >
                    <span className="mgmt-demo__bar-label">{t.status}</span>
                  </span>
                );
              })}

            {width > 0 && (
              <span className="mgmt-demo__today" style={{ left: todayX, height: chartH }}>
                <span className="mgmt-demo__today-flag">Today</span>
              </span>
            )}
          </div>
        </div>
      </div>

      <p className="mgmt-demo__caption">
        Illustrative project schedule — dependency-linked tasks, rolled-up parents,
        and milestones on one timeline.
      </p>
    </div>
  );
};

export default ManagementDemo;
